/**
 * Whether a sign-in was interrupted, and the one error that means "update the app".
 *
 * Its own module, and deliberately free of the WebAuthn browser library, so the reasoning here
 * can be tested outside a browser — which is where it was decided, and where it was wrong twice.
 */
import { forget, recall, remember } from './offline.js';

/**
 * A sign-in that started and never came back.
 *
 * Android may reclaim the app while its fingerprint sheet is up: the app is rebuilt, the page
 * loads again, and the promise waiting for the answer went with the old page. From the outside
 * that is a screen which refreshed and did nothing — the complaint that keeps returning, and the
 * one thing on that screen nobody could tell apart from an ordinary visit.
 *
 * It rides on `remember`, which is localStorage, rather than on sessionStorage: when Android
 * takes the app away entirely, a session store goes with it, and this has to survive exactly the
 * case it exists to explain. Signing out clears it with everything else.
 */
const KEY = 'passkeyStarted';

/**
 * Longer than any prompt, shorter than a shift. A phone left on the sign-in screen overnight and
 * picked up in the morning must not be told its sign-in was interrupted.
 */
const INTERRUPTED_WITHIN_MS = 3 * 60 * 1000;

export function markCeremony(started: boolean): void {
  if (started) remember(KEY, true);
  else forget(KEY);
}

/** True when the last load interrupted a sign-in in progress. Reading it clears it. */
export function signInWasInterrupted(): boolean {
  const last = recall<boolean>(KEY);
  if (!last) return false;
  forget(KEY);
  const at = new Date(last.at).getTime();
  return Number.isFinite(at) && Date.now() - at < INTERRUPTED_WITHIN_MS;
}

/**
 * An app too old to ask Android for the passkey itself, told apart from every other failure.
 *
 * Such an app has only the WebView route, which is the route that fails on some phones. No deploy
 * fixes it, because the fix is in the app — so the screen has to say so rather than offering the
 * advice it gives somebody who dismissed a prompt.
 */
export class AppTooOldError extends Error {
  override readonly name = 'AppTooOldError';
  constructor() {
    super('app too old for native passkey sign-in');
  }
}
